import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Search } from "lucide-react";
import { getActivationArchive, type TodayActivation } from "@/lib/activations-today.functions";
import { ReleaseCover } from "@/components/site/ReleaseCover";
import { absoluteUrl } from "@/lib/site";
import { ACTIVATIONS_ID, breadcrumbs, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

export const Route = createFileRoute("/activations/archive")({
  component: ArchivePage,
  head: () => ({
    ...seoMeta({
      title: "Activation Archive · KAfrequency",
      description:
        "Every published KAfrequency daily activation — searchable, filterable, and preserved.",
      path: "/activations/archive",
    }),
    scripts: [
      ldScript(
        pageNode({
          type: "CollectionPage",
          id: `${absoluteUrl("/activations/archive")}#archive`,
          url: absoluteUrl("/activations/archive"),
          name: "Activation Archive",
          description:
            "The complete archive of published KAfrequency daily activations by Buddha KA.",
          about: [personRef(), orgRef()],
          extra: { isPartOf: { "@id": ACTIVATIONS_ID } },
        }),
      ),
      ldScript(
        breadcrumbs([["Activations", "/activations"], ["Archive", "/activations/archive"]]),
      ),
    ],
  }),
});

const PAGE = 24;

function ArchivePage() {
  const [rows, setRows] = useState<TodayActivation[]>([]);
  const [count, setCount] = useState(0);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [gate, setGate] = useState<string>("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const fetchArchive = useServerFn(getActivationArchive);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await fetchArchive({
          data: {
            q: q.trim() || undefined,
            tag: tag.trim() || undefined,
            gate: gate ? Number(gate) : undefined,
            limit: PAGE,
            offset: page * PAGE,
          },
        });
        if (cancelled) return;
        setRows(r.rows);
        setCount(r.count);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q, tag, gate, page, fetchArchive]);

  const pages = Math.max(1, Math.ceil(count / PAGE));

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
      <header className="mb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Archive</p>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl">Activation Archive</h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Every daily activation, preserved. Revisit a practice, a gate, or a theme at any time.
        </p>
      </header>

      <div className="mb-8 grid gap-3 sm:grid-cols-[1fr_180px_140px]">
        <label className="relative">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            placeholder="Search title…"
            className="w-full rounded-full border hairline bg-transparent py-2 pl-9 pr-4 text-sm focus:border-gold focus:outline-none"
          />
        </label>
        <input
          value={tag}
          onChange={(e) => { setTag(e.target.value); setPage(0); }}
          placeholder="Tag (e.g. discipline)"
          className="rounded-full border hairline bg-transparent px-4 py-2 text-sm focus:border-gold focus:outline-none"
        />
        <input
          value={gate}
          onChange={(e) => { setGate(e.target.value); setPage(0); }}
          placeholder="Gate #"
          inputMode="numeric"
          className="rounded-full border hairline bg-transparent px-4 py-2 text-sm focus:border-gold focus:outline-none"
        />
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No activations match those filters yet.</p>
      ) : (
        <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((a) => (
            <li key={a.id}>
              <Link
                to="/activations/$slug"
                params={{ slug: a.slug }}
                className="group block overflow-hidden rounded-xl border hairline bg-charcoal transition hover:border-gold"
              >
                <ReleaseCover coverUrl={a.hero_image_url} label={a.title} aspect="video" alt={a.title} />
                <div className="p-4">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold">
                    {a.activation_number ? `№ ${a.activation_number}` : "Activation"}
                    {a.publish_at ? ` · ${new Date(a.publish_at).toLocaleDateString()}` : ""}
                  </p>
                  <h2 className="mt-2 font-serif text-xl text-gold-luminous group-hover:underline">{a.title}</h2>
                  {a.intention && <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{a.intention}</p>}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {pages > 1 && (
        <nav aria-label="Archive pagination" className="mt-10 flex items-center justify-between text-xs uppercase tracking-[0.2em]">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="rounded-full border hairline px-4 py-2 disabled:opacity-40 hover:border-gold hover:text-gold"
          >
            ← Previous
          </button>
          <span className="text-muted-foreground">Page {page + 1} of {pages}</span>
          <button
            disabled={page + 1 >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-full border hairline px-4 py-2 disabled:opacity-40 hover:border-gold hover:text-gold"
          >
            Next →
          </button>
        </nav>
      )}
    </div>
  );
}
